/**
 * JobsPlannerPage — Workshop Job Planner (Gantt / Timeline view)
 *
 * Changes from v3:
 * ─────────────────
 * • Two-level grouping: top-level sections "Active Jobs" and "Completed
 *   Jobs", each containing staff sub-groups with jobs sorted by priority
 *   (URGENT first) then planner order descending (biggest # on top).
 * • Completed section collapsed by default.
 * • Left panel widened (420px) so the planner order −/+/number controls
 *   are visible without horizontal scrolling.
 * • Planner order editor buttons enlarged with borders for better
 *   visibility and touch targets.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    GanttChart,
    ChevronLeft,
    ChevronRight,
    ChevronDown,
    ChevronUp,
    RefreshCw,
    Clock,
    AlertTriangle,
    ZoomIn,
    ZoomOut,
    Calendar,
    User,
    Users,
    Car,
    Pencil,
    Check,
    X,
} from 'lucide-react';
import Layout from './Layout';
import JobsToolbar from './JobsToolbar';
import { fetchJobs, updateJob, updateJobPlannerOrder, assignStaff, unassignStaff } from '../store/slices/jobSlice';
import useQueuedJobs from '../hooks/useQueuedJobs';
import PagedSearchControls from './PagedSearchControls';
import useSearchBox from '../hooks/useSearchBox';
import LaneLoading from './LaneLoading';
import { filterByNameOrRego } from '../utils/localSearch';
import { isQueuedStatus } from '../utils/queuedStatuses';
import {
    fetchAllLeavePlansInRange,
    fetchStaffList,
    selectLeavePlansByStaff,
    selectStaffList,
} from '../store/slices/staffSlice';

// ─── Constants ────────────────────────────────────────────────────────────────

const MIN_CELL_PX    = 40;
const DEFAULT_CELL   = 80;
const MAX_CELL_PX    = 200;
const ROW_H          = 96;
const COLLAPSED_ROW_H = 36;  // Compact single-row height when staff group is collapsed
const GROUP_HEADER_H = 34;
const SECTION_HEADER_H = 38;  // Top-level Active / Completed section headers
const HEADER_H       = 52;
const LABEL_W        = 420;
const DEFAULT_WINDOW = 14;

/**
 * Days of the recent past kept in view to the left of today when the planner opens.
 *
 * Two, not none: a job that ran yesterday is context for today's, and a timeline whose
 * first column IS today reads as though nothing came before it.
 */
const TODAY_LEAD_DAYS = 2;
const MIN_BAR_PX     = 24;
const LEGEND_H       = 56;   // Fixed legend area — easy to tweak / redesign
const PAN_CLICK_SLOP = 4;    // px of drag before a grab-pan stops counting as a click

// Sort order arrays
const PRIORITY_ORDER = ['URGENT', 'HIGH', 'NORMAL', 'LOW'];
const STATUS_ORDER   = ['IN_PROGRESS', 'PAUSED', 'APPROVED', 'QUOTED', 'CREATED', 'COMPLETED', 'INVOICED'];

// Sentinel key used for the "Unassigned" bucket
const UNASSIGNED_KEY = '__unassigned__';

// Completion-based top-level section keys
const SECTION_ACTIVE    = '__section_active__';
const SECTION_COMPLETED = '__section_completed__';
const COMPLETED_STATUSES = ['COMPLETED', 'INVOICED'];

// ─── Staff colour palette ─────────────────────────────────────────────────────

/**
 * Each entry provides Tailwind classes for the bar (bg / border / text) plus a
 * plain hex swatch for the legend / group header. The palette is deliberately
 * distinct — no two adjacent hues — so rows are easy to tell apart.
 */
const STAFF_PALETTE = [
    { bg: 'bg-violet-500',  border: 'border-violet-600',  text: 'text-white', swatch: '#8b5cf6', tint: 'bg-violet-50'  },
    { bg: 'bg-sky-500',     border: 'border-sky-600',     text: 'text-white', swatch: '#0ea5e9', tint: 'bg-sky-50'     },
    { bg: 'bg-emerald-500', border: 'border-emerald-600', text: 'text-white', swatch: '#10b981', tint: 'bg-emerald-50' },
    { bg: 'bg-pink-500',    border: 'border-pink-600',    text: 'text-white', swatch: '#ec4899', tint: 'bg-pink-50'    },
    { bg: 'bg-indigo-500',  border: 'border-indigo-600',  text: 'text-white', swatch: '#6366f1', tint: 'bg-indigo-50'  },
    { bg: 'bg-teal-500',    border: 'border-teal-600',    text: 'text-white', swatch: '#14b8a6', tint: 'bg-teal-50'    },
    { bg: 'bg-cyan-500',    border: 'border-cyan-600',    text: 'text-white', swatch: '#06b6d4', tint: 'bg-cyan-50'    },
    { bg: 'bg-fuchsia-500', border: 'border-fuchsia-600', text: 'text-white', swatch: '#d946ef', tint: 'bg-fuchsia-50' },
    { bg: 'bg-lime-600',    border: 'border-lime-700',    text: 'text-white', swatch: '#65a30d', tint: 'bg-lime-50'    },
    { bg: 'bg-rose-500',    border: 'border-rose-600',    text: 'text-white', swatch: '#f43f5e', tint: 'bg-rose-50'    },
    { bg: 'bg-blue-500',    border: 'border-blue-600',    text: 'text-white', swatch: '#3b82f6', tint: 'bg-blue-50'    },
    { bg: 'bg-purple-600',  border: 'border-purple-700',  text: 'text-white', swatch: '#9333ea', tint: 'bg-purple-50'  },
];

const UNASSIGNED_COLOUR = {
    bg: 'bg-gray-400', border: 'border-gray-500', text: 'text-white',
    swatch: '#9ca3af', tint: 'bg-gray-100',
};

/** Deterministic hash → palette index so the same staff keeps the same colour. */
const hashString = (s) => {
    let h = 0;
    for (let i = 0; i < s.length; i++) {
        h = ((h << 5) - h) + s.charCodeAt(i);
        h |= 0;
    }
    return Math.abs(h);
};

// ─── Date helpers ─────────────────────────────────────────────────────────────

/**
 * Parse "YYYY-MM-DD" as LOCAL midnight.
 * new Date("2026-03-26") is UTC midnight → shifted back to 25 Mar in AEST (UTC+11).
 * Constructing from parts avoids that timezone shift entirely.
 */
const parseLocalDate = (iso) => {
    if (!iso) return null;
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d, 0, 0, 0, 0);
};

const startOfDay = (d) => {
    const r = new Date(d);
    r.setHours(0, 0, 0, 0);
    return r;
};

const addDays = (d, n) => {
    const r = new Date(d);
    r.setDate(r.getDate() + n);
    return r;
};

const diffDays = (a, b) =>
    Math.round((startOfDay(a) - startOfDay(b)) / 86_400_000);

/**
 * Format a Date to "YYYY-MM-DD" using LOCAL date parts.
 * d.toISOString() converts to UTC first, shifting the date back in AEST.
 */
const toIsoDate = (d) => {
    const y  = d.getFullYear();
    const m  = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
};

const fmtDay = (d) =>
    d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });

const fmtShortDay = (d) =>
    d.toLocaleDateString('en-AU', { day: 'numeric' });

const fmtMonth = (d) =>
    d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });

// ─── Business logic ───────────────────────────────────────────────────────────

/**
 * Bar start date: prefer scheduledStartDate (what the planner writes),
 * fall back to actualStartDate → bookingDate → today.
 */
const getBarStart = (job) => {
    if (job.scheduledStartDate) return startOfDay(parseLocalDate(job.scheduledStartDate));
    if (job.actualStartDate)    return startOfDay(parseLocalDate(job.actualStartDate));
    if (job.bookingDate)        return startOfDay(parseLocalDate(job.bookingDate));
    return startOfDay(new Date());
};

/**
 * Bar width in fractional days.
 * If both scheduledStart/End are present, use that span.
 * Otherwise fall back to estimatedDurationMinutes (8-hr day), defaulting to 1 day.
 */
const getDurationDays = (job) => {
    if (job.scheduledStartDate && job.scheduledEndDate) {
        const d = diffDays(parseLocalDate(job.scheduledEndDate), parseLocalDate(job.scheduledStartDate));
        if (d > 0) return d;
    }
    if (job.estimatedDurationMinutes && job.estimatedDurationMinutes > 0) {
        return Math.max(0.5, job.estimatedDurationMinutes / 60 / 8);
    }
    return 1;
};

/**
 * Bar colour — priority overrides staff colour for URGENT / HIGH so critical
 * work still stands out. Otherwise bar is coloured by staff.
 */
const barStyle = (job, staffColour) => {
    if (job.priority === 'URGENT') return { bg: 'bg-red-500',    border: 'border-red-600',    text: 'text-white' };
    if (job.priority === 'HIGH')   return { bg: 'bg-orange-400', border: 'border-orange-500', text: 'text-white' };
    return staffColour;
};

// ─── PriorityDropdown ────────────────────────────────────────────────────────

const PRIORITY_META = {
    URGENT: { label: 'Urgent', dot: 'bg-red-500',    text: 'text-red-600'    },
    HIGH:   { label: 'High',   dot: 'bg-orange-400', text: 'text-orange-500' },
    NORMAL: { label: 'Normal', dot: 'bg-gray-400',   text: 'text-gray-500'   },
    LOW:    { label: 'Low',    dot: 'bg-blue-400',   text: 'text-blue-500'   },
};

const PriorityDropdown = ({ priority, saving, onChangePriority }) => {
    const current = PRIORITY_META[priority] ?? PRIORITY_META.NORMAL;

    if (saving) {
        return (
            <div className="shrink-0 w-[84px] flex items-center justify-center">
                <RefreshCw size={11} className="animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="shrink-0 relative" onClick={(e) => e.stopPropagation()}>
            <div className={`absolute left-2 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full pointer-events-none z-10 ${current.dot}`} />
            <select
                value={priority ?? 'NORMAL'}
                onChange={(e) => onChangePriority(e.target.value)}
                className={`pl-5 pr-2 py-1 text-[11px] font-semibold rounded-lg border border-gray-200
                    bg-white appearance-none cursor-pointer focus:outline-none focus:ring-1
                    focus:ring-violet-400 hover:border-gray-300 transition-colors ${current.text}`}
            >
                {Object.entries(PRIORITY_META).map(([value, meta]) => (
                    <option key={value} value={value}>{meta.label}</option>
                ))}
            </select>
        </div>
    );
};


// ─── DateEditField ────────────────────────────────────────────────────────────

/**
 * Inline date editor with an edit (pencil) button.
 * Shows the formatted date with a pencil icon; clicking pencil reveals a
 * native <input type="date"> inline. Confirm with ✓ or cancel with ✕.
 */
const DateEditField = ({ label, isoDate, onSave, saving }) => {
    const [editing,  setEditing]  = useState(false);
    const [value,    setValue]    = useState(isoDate ?? '');

    // Sync if parent changes the date (e.g. after a drag)
    useEffect(() => {
        if (!editing) setValue(isoDate ?? '');
    }, [isoDate, editing]);

    const handleConfirm = (e) => {
        e.stopPropagation();
        if (value && value !== isoDate) onSave(value);
        setEditing(false);
    };

    const handleCancel = (e) => {
        e.stopPropagation();
        setValue(isoDate ?? '');
        setEditing(false);
    };

    const displayDate = isoDate
        ? parseLocalDate(isoDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
        : 'Not set';

    if (saving) {
        return (
            <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold">{label}</span>
                <RefreshCw size={10} className="animate-spin text-violet-400" />
            </div>
        );
    }

    return (
        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            <span className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold shrink-0">{label}</span>
            {editing ? (
                <div className="flex items-center gap-1">
                    <input
                        type="date"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        autoFocus
                        className="text-[11px] font-medium text-gray-800 border border-violet-400 rounded px-1.5 py-0.5
                            focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter')  handleConfirm(e);
                            if (e.key === 'Escape') handleCancel(e);
                        }}
                    />
                    <button
                        onClick={handleConfirm}
                        className="p-0.5 rounded text-emerald-600 hover:bg-emerald-50 transition-colors"
                        title="Confirm"
                    >
                        <Check size={12} />
                    </button>
                    <button
                        onClick={handleCancel}
                        className="p-0.5 rounded text-gray-400 hover:bg-gray-100 transition-colors"
                        title="Cancel"
                    >
                        <X size={12} />
                    </button>
                </div>
            ) : (
                <div className="flex items-center gap-1 group/date">
                    <span className="text-[11px] font-semibold text-gray-700">{displayDate}</span>
                    <button
                        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
                        className="p-0.5 rounded text-gray-300 hover:text-violet-500 hover:bg-violet-50
                            opacity-0 group-hover/date:opacity-100 transition-all"
                        title={`Edit ${label}`}
                    >
                        <Pencil size={10} />
                    </button>
                </div>
            )}
        </div>
    );
};

// ─── PlannerOrderEditor ───────────────────────────────────────────────────────

/**
 * Compact inline number editor for the planner sequence number.
 * Shows the current value (or '–' if unset) with – / + buttons.
 * Clicking the number itself opens a small text input for direct entry.
 * Fires onSave(newValue) only when the value actually changes.
 */
const PlannerOrderEditor = ({ value, saving, onSave }) => {
    const [editing, setEditing] = useState(false);
    const [draft,   setDraft]   = useState('');

    const display = value != null ? String(value) : '–';

    const commit = (raw) => {
        const n = parseInt(raw, 10);
        if (!isNaN(n) && n >= 1 && n !== value) onSave(n);
        setEditing(false);
    };

    const decrement = (e) => {
        e.stopPropagation();
        const next = Math.max(1, (value ?? 1) - 1);
        if (next !== value) onSave(next);
    };

    const increment = (e) => {
        e.stopPropagation();
        const next = (value ?? 0) + 1;
        onSave(next);
    };

    if (saving) {
        return (
            <div className="flex items-center gap-1 shrink-0">
                <RefreshCw size={10} className="animate-spin text-violet-400" />
            </div>
        );
    }

    return (
        <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
            <span className="text-[9px] text-gray-400 uppercase tracking-wide font-semibold mr-0.5">#</span>
            <button
                onClick={decrement}
                disabled={value == null || value <= 1}
                className="w-5 h-5 flex items-center justify-center rounded text-gray-400
                    hover:text-violet-600 hover:bg-violet-50 disabled:opacity-30 disabled:cursor-not-allowed
                    transition-colors text-[12px] font-bold leading-none border border-gray-200 hover:border-violet-300"
                title="Decrease order"
            >−</button>
            {editing ? (
                <input
                    type="number"
                    min="1"
                    autoFocus
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={(e) => commit(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter')  commit(draft);
                        if (e.key === 'Escape') setEditing(false);
                    }}
                    className="w-10 text-center text-[11px] font-bold text-violet-700 border border-violet-400
                        rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white"
                />
            ) : (
                <button
                    onClick={(e) => { e.stopPropagation(); setDraft(value != null ? String(value) : ''); setEditing(true); }}
                    className="min-w-[28px] text-center text-[11px] font-bold text-violet-700 hover:bg-violet-50
                        rounded px-1.5 py-0.5 transition-colors border border-violet-200"
                    title="Click to edit planner order"
                >
                    {display}
                </button>
            )}
            <button
                onClick={increment}
                className="w-5 h-5 flex items-center justify-center rounded text-gray-400
                    hover:text-violet-600 hover:bg-violet-50 transition-colors text-[12px] font-bold leading-none border border-gray-200 hover:border-violet-300"
                title="Increase order"
            >+</button>
        </div>
    );
};

// ─── StatusBadge ─────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
    const map = {
        CREATED:     'bg-gray-100 text-gray-600',
        QUOTED:      'bg-blue-100 text-blue-700',
        APPROVED:    'bg-sky-100 text-sky-700',
        IN_PROGRESS: 'bg-violet-100 text-violet-700',
        PAUSED:      'bg-amber-100 text-amber-700',
        COMPLETED:   'bg-emerald-100 text-emerald-700',
        INVOICED:    'bg-teal-100 text-teal-700',
        CANCELLED:   'bg-red-100 text-red-600',
    };
    return (
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide shrink-0 ${map[status] || map.CREATED}`}>
            {status?.replace('_', ' ') ?? '—'}
        </span>
    );
};

// ─── LeaveOverlay ─────────────────────────────────────────────────────────────

/**
 * Inline staff assignment dropdown.
 * Shows the currently assigned staff name (or "Unassigned") with a select to
 * pick a new staff member. Selecting "— Unassigned —" triggers unassign.
 * The component uses the staffList already fetched into Redux.
 */
const StaffAssignDropdown = ({ job, staffList, staffColourMap, saving, onAssign, onUnassign }) => {
    const currentUuid = job.assignedStaffUuid || '';
    const colour = staffColourMap[currentUuid || UNASSIGNED_KEY] || UNASSIGNED_COLOUR;

    if (saving) {
        return (
            <div className="flex items-center gap-1.5 shrink-0">
                <RefreshCw size={10} className="animate-spin text-violet-400" />
                <span className="text-[10px] text-gray-400">Assigning…</span>
            </div>
        );
    }

    const handleChange = (e) => {
        e.stopPropagation();
        const newUuid = e.target.value;
        if (newUuid === '' && currentUuid) {
            onUnassign(job);
        } else if (newUuid && newUuid !== currentUuid) {
            onAssign(job, newUuid);
        }
    };

    return (
        <div className="flex items-center gap-1.5 w-full min-w-0" onClick={(e) => e.stopPropagation()}>
            <div
                className="w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: colour.swatch }}
            />
            <Users size={10} className="shrink-0 text-gray-400" />
            <select
                value={currentUuid}
                onChange={handleChange}
                className={`text-[11px] font-medium rounded border border-gray-200 bg-white
                    pl-1 pr-4 py-0.5 appearance-none cursor-pointer truncate min-w-0 flex-1
                    focus:outline-none focus:ring-1 focus:ring-violet-400 hover:border-gray-300
                    transition-colors ${currentUuid ? 'text-gray-700' : 'text-gray-400 italic'}`}
                title={currentUuid
                    ? `Assigned: ${job.assignedStaffName || 'Unknown'}`
                    : 'Click to assign staff'}
            >
                <option value="">— Unassigned —</option>
                {(staffList ?? []).map(s => {
                    const name = s.fullName || `${s.firstName ?? ''} ${s.lastName ?? ''}`.trim() || 'Unnamed';
                    return (
                        <option key={s.uuid} value={s.uuid}>{name}</option>
                    );
                })}
            </select>
        </div>
    );
};

/**
 * Renders "on leave" blocks for a single staff group as an absolutely-positioned
 * overlay. Meant to be placed inside a `relative` wrapper that spans all job
 * rows belonging to one staff group. Uses the same cellPx / originDate math
 * as GanttBar so blocks line up with the day grid exactly.
 *
 * Each block is diagonal-hatched grey with a tooltip showing the date range.
 */
const LeaveOverlay = ({ leavePlans, cellPx, originDate, windowFromDate, windowToDate }) => {
    if (!leavePlans || leavePlans.length === 0) return null;

    return (
        <div className="absolute inset-0 pointer-events-none z-[5]">
            {leavePlans.map(lp => {
                const lpStart = parseLocalDate(lp.startDate);
                const lpEnd   = parseLocalDate(lp.endDate);

                // Clip to visible window so blocks don't render off-grid
                const visibleStart = lpStart < windowFromDate ? windowFromDate : lpStart;
                const visibleEnd   = lpEnd   > windowToDate   ? windowToDate   : lpEnd;
                if (visibleEnd < visibleStart) return null;

                const offset = diffDays(visibleStart, originDate);
                // +1 because end date is inclusive
                const spanDays = diffDays(visibleEnd, visibleStart) + 1;

                const left  = offset * cellPx;
                const width = spanDays * cellPx;

                const tooltip = `On leave: ${fmtDay(lpStart)} → ${fmtDay(lpEnd)}`;

                return (
                    <div
                        key={lp.uuid}
                        className="absolute top-0 bottom-0 pointer-events-auto"
                        style={{
                            left,
                            width,
                            // Subtle grey diagonal hatching + tint
                            backgroundImage:
                                'repeating-linear-gradient(135deg, rgba(107, 114, 128, 0.18) 0px, rgba(107, 114, 128, 0.18) 6px, rgba(156, 163, 175, 0.08) 6px, rgba(156, 163, 175, 0.08) 12px)',
                            borderLeft:  '2px dashed rgba(107, 114, 128, 0.55)',
                            borderRight: '2px dashed rgba(107, 114, 128, 0.55)',
                        }}
                        title={tooltip}
                    >
                        <div className="sticky left-2 top-1 inline-flex items-center gap-1 px-1.5 py-0.5 bg-white/85 rounded text-[10px] font-semibold text-gray-600 uppercase tracking-wider border border-gray-300 shadow-sm whitespace-nowrap">
                            On leave
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

/** True when a mouse event landed on an element's native scrollbar gutter. */
const onScrollbar = (el, e) => {
    const r = el.getBoundingClientRect();
    return (e.clientX - r.left) > el.clientWidth || (e.clientY - r.top) > el.clientHeight;
};

// ─── GanttBar ─────────────────────────────────────────────────────────────────

/**
 * Draggable / resizable bar.
 * onUpdate(job, newScheduledStartDate, newScheduledEndDate, newDurationMinutes)
 */
const GanttBar = ({ job, cellPx, originDate, staffColour, onUpdate, onSelect, compact = false }) => {
    const dragRef = useRef(null);

    const barStart     = getBarStart(job);
    const durationDays = getDurationDays(job);
    const offsetDays   = diffDays(barStart, originDate);
    const initLeft     = offsetDays * cellPx;
    const initWidth    = Math.max(MIN_BAR_PX, durationDays * cellPx);

    const [pos, setPos] = useState({ left: initLeft, width: initWidth });

    // Re-sync from server data after save, or when zoom changes
    useEffect(() => {
        setPos({
            left:  Math.max(0, offsetDays * cellPx),
            width: Math.max(MIN_BAR_PX, durationDays * cellPx),
        });
    }, [cellPx, job.scheduledStartDate, job.scheduledEndDate, job.estimatedDurationMinutes, offsetDays, durationDays]);

    const startDrag = (e, mode) => {
        if (compact) return; // No drag in compact mode
        e.stopPropagation();
        e.preventDefault();
        dragRef.current = { mode, startX: e.clientX, startLeft: pos.left, startWidth: pos.width };

        const onMove = (ev) => {
            const dx = ev.clientX - dragRef.current.startX;
            setPos(p =>
                dragRef.current.mode === 'move'
                    ? { ...p, left:  Math.max(0, dragRef.current.startLeft + dx) }
                    : { ...p, width: Math.max(MIN_BAR_PX, dragRef.current.startWidth + dx) }
            );
        };

        const onUp = (ev) => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);

            const dx = ev.clientX - dragRef.current.startX;
            const rawLeft  = dragRef.current.mode === 'move'
                ? Math.max(0, dragRef.current.startLeft + dx)
                : dragRef.current.startLeft;
            const rawWidth = dragRef.current.mode === 'resize'
                ? Math.max(MIN_BAR_PX, dragRef.current.startWidth + dx)
                : dragRef.current.startWidth;

            // Snap: move → floor so bar lands in the column its left edge is inside.
            // resize → 0.5-day precision.
            const snappedOffset   = Math.floor(rawLeft / cellPx);
            const snappedDuration = Math.max(0.5, Math.round((rawWidth / cellPx) * 2) / 2);

            const newStart   = addDays(originDate, snappedOffset);
            const newEnd     = addDays(newStart, Math.ceil(snappedDuration));
            const newMinutes = Math.round(snappedDuration * 8 * 60);

            setPos({
                left:  snappedOffset * cellPx,
                width: Math.max(MIN_BAR_PX, snappedDuration * cellPx),
            });

            onUpdate(job, newStart, newEnd, newMinutes);
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    const s = barStyle(job, staffColour);

    return (
        <div
            className={`absolute rounded-lg border flex items-center select-none group
                ${compact
                ? 'top-1 h-[22px] rounded-md cursor-pointer shadow-sm hover:shadow'
                : 'top-2 h-9 cursor-grab active:cursor-grabbing shadow-sm hover:shadow-md'
            } transition-shadow
                ${s.bg} ${s.border} ${s.text}`}
            style={{ left: pos.left, width: pos.width, zIndex: 10 }}
            data-planner-bar="true"
            onMouseDown={compact ? undefined : (e) => startDrag(e, 'move')}
            onClick={(e) => { e.stopPropagation(); onSelect(job); }}
            title={`${job.jobNumber} — ${job.customerName}\n${fmtDay(getBarStart(job))} · ${getDurationDays(job).toFixed(1)} days\nAssigned: ${job.assignedStaffName || 'Unassigned'}`}
        >
            <div className={`flex items-center gap-1 overflow-hidden flex-1 min-w-0
                ${compact ? 'px-1.5' : 'px-2 gap-1.5'}`}>
                <span className={`font-bold whitespace-nowrap ${compact ? 'text-[9px]' : 'text-[11px]'}`}>{job.jobNumber}</span>
                {!compact && pos.width > 110 && (
                    <span className="text-[11px] opacity-80 truncate">{job.customerName}</span>
                )}
                {!compact && pos.width > 200 && job.vehicleRego && (
                    <span className="text-[10px] opacity-60 font-mono shrink-0">{job.vehicleRego}</span>
                )}
            </div>

            {/* Resize handle — not shown in compact mode */}
            {!compact && (
                <div
                    className="absolute right-0 top-0 h-full w-3 cursor-ew-resize flex items-center justify-center
                        opacity-0 group-hover:opacity-100 transition-opacity"
                    onMouseDown={(e) => startDrag(e, 'resize')}
                >
                    <div className="w-0.5 h-5 rounded-full bg-white/60" />
                    <div className="w-0.5 h-5 rounded-full bg-white/60 ml-0.5" />
                </div>
            )}
        </div>
    );
};

// ─── JobsPlannerPage ──────────────────────────────────────────────────────────

const JobsPlannerPage = ({ onBack, onSwitchToTablet }) => {
    const dispatch  = useDispatch();
    const { jobs, jobsLoading } = useSelector(state => state.jobs);
    const leavePlansByStaff = useSelector(selectLeavePlansByStaff);
    const staffList         = useSelector(selectStaffList);

    const [cellPx,     setCellPx]     = useState(DEFAULT_CELL);
    const [windowDays]                = useState(DEFAULT_WINDOW);
    const [offsetDays, setOffsetDays] = useState(0);
    const [saving,     setSaving]     = useState({});  // { [uuid]: 'schedule'|'priority' }
    const [toast,      setToast]      = useState(null);
    const [selected,   setSelected]   = useState(null);
    const [collapsedGroups, setCollapsedGroups] = useState({}); // { [groupKey]: true }
    const [collapsedSections, setCollapsedSections] = useState({ [SECTION_COMPLETED]: true });

    // 🔑 The queued jobs come from their own page, never from the month query — which
    // is why the Active section used to show only the first twenty of the month. The
    // text narrows that page on the keystroke; the settled term asks the server, which
    // is where the other pages are. See useSearchBox.
    const queuedBox = useSearchBox();
    const queuedJobs = useQueuedJobs({ q: queuedBox.q });
    // The active list waits on BOTH its sources. "No active jobs" is a claim, and is
    // only made once the month query and the queued page have both answered.
    const plannerLoading = jobsLoading || queuedJobs.loading;

    const toggleGroup = useCallback((key) => {
        setCollapsedGroups(prev => ({ ...prev, [key]: !prev[key] }));
    }, []);

    const toggleSection = useCallback((key) => {
        setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
    }, []);

    // ── Click-and-hold panning of the timeline canvas ────────────────────────
    // Grab anywhere on the empty grid and drag to move the calendar in both
    // axes. Bars stopPropagation on mousedown, so a bar drag always wins; the
    // data-planner-bar guard covers compact bars, which have no drag handler.
    const timelineScrollRef = useRef(null);  // horizontal scroller
    const gridScrollRef     = useRef(null);  // vertical scroller (right pane)
    const labelScrollRef    = useRef(null);  // vertical scroller (left pane)
    const panRef            = useRef(null);
    const [panning, setPanning] = useState(false);

    const startPan = useCallback((e) => {
        if (e.button !== 0) return;                                // left button only
        if (e.target.closest?.('[data-planner-bar]')) return;      // let bars handle it
        const hx = timelineScrollRef.current;
        if (!hx) return;
        const vy = gridScrollRef.current;
        const ly = labelScrollRef.current;

        // Ignore presses that landed on a native scrollbar gutter — those must
        // stay draggable as scrollbars.
        if (onScrollbar(hx, e) || (vy && onScrollbar(vy, e))) return;

        e.preventDefault();                                      // no text selection while dragging
        panRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            startLeft: hx.scrollLeft,
            startTop:  vy ? vy.scrollTop : 0,
            startLabelTop: ly ? ly.scrollTop : 0,
            moved: false,
        };
        setPanning(true);

        const onMove = (ev) => {
            const p = panRef.current;
            if (!p) return;
            const dx = ev.clientX - p.startX;
            const dy = ev.clientY - p.startY;
            if (!p.moved && Math.abs(dx) + Math.abs(dy) > PAN_CLICK_SLOP) p.moved = true;
            hx.scrollLeft = p.startLeft - dx;
            // Keep the left job list locked to the same rows as the timeline.
            if (vy) vy.scrollTop = p.startTop - dy;
            if (ly) ly.scrollTop = p.startLabelTop - dy;
        };

        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            setPanning(false);
            // Swallow the click that follows a real drag, otherwise releasing
            // over a section/staff header collapses it.
            if (panRef.current?.moved) {
                const swallow = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
                window.addEventListener('click', swallow, { capture: true, once: true });
                setTimeout(() => window.removeEventListener('click', swallow, true), 0);
            }
            panRef.current = null;
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    }, []);

    // Keep detail drawer in sync after Redux updates
    const jobsById = Object.fromEntries((jobs ?? []).map(j => [j.uuid, j]));
    useEffect(() => {
        if (selected?.uuid && jobsById[selected.uuid]) {
            setSelected(jobsById[selected.uuid]);
        }
    }, [jobs]);

    const today      = startOfDay(new Date());
    const originDate = addDays(today, -windowDays + offsetDays);
    const totalDays  = windowDays * 2 + 1;

    useEffect(() => { dispatch(fetchJobs()); }, [dispatch]);

    // Fetch the active staff list so we can build lanes for staff who have
    // leave in the window but no jobs assigned.
    useEffect(() => {
        dispatch(fetchStaffList({ activeOnly: true, size: 200 }));
    }, [dispatch]);

    // Fetch all leave plans in the current visible window so we can overlay
    // "on leave" blocks on each staff lane. Re-fires whenever the user pans
    // or changes the window size.
    useEffect(() => {
        const from = toIsoDate(originDate);
        const to   = toIsoDate(addDays(originDate, totalDays - 1));
        dispatch(fetchAllLeavePlansInRange({ from, to }));
    }, [dispatch, offsetDays, windowDays]);

    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 3500);
        return () => clearTimeout(t);
    }, [toast]);

    // All displayable jobs: the queued page (narrowed locally) plus the month's
    // non-queued work, excluding only terminal VEHICLE_COLLECTED and CANCELLED. A
    // CREATED or QUOTED job in the month payload is skipped — it is on the queued page.
    const displayableJobs = [
        ...filterByNameOrRego(queuedJobs.jobs, queuedBox.text),
        ...(jobs ?? []).filter(
            j => !['VEHICLE_COLLECTED', 'CANCELLED'].includes(j.status) && !isQueuedStatus(j.status)
        ),
    ];

    // Sort: priority (URGENT first) → plannerOrder descending (biggest on top) → jobNumber
    const sortJobs = (list) => [...list].sort((a, b) => {
        const pa = PRIORITY_ORDER.indexOf(a.priority ?? 'NORMAL');
        const pb = PRIORITY_ORDER.indexOf(b.priority ?? 'NORMAL');
        if (pa !== pb) return pa - pb;
        const oa = a.plannerOrder ?? -1;
        const ob = b.plannerOrder ?? -1;
        if (oa !== ob) return ob - oa;
        return (a.jobNumber ?? '').localeCompare(b.jobNumber ?? '');
    });

    // ── Two-level grouping: Section (Active/Completed) → Staff → Jobs ─────────
    /**
     * Build a two-level structure:
     *   sections[]: { sectionKey, sectionName, staffGroups[] }
     *     staffGroups[]: { key, uuid, name, jobs[] }
     *
     * Staff colour mapping is deterministic (hash-based) across both sections
     * so bars keep consistent colours.
     *
     * Groups are also seeded from staff who have leave in the window.
     */
    const { sections, staffColourMap } = useMemo(() => {
        const isCompleted = (job) => COMPLETED_STATUSES.includes(job.status);

        // Partition jobs
        const activeJobList    = displayableJobs.filter(j => !isCompleted(j));
        const completedJobList = displayableJobs.filter(j => isCompleted(j));

        // Build staff sub-groups from a list of jobs
        const buildStaffGroups = (jobList) => {
            const groups = new Map();
            for (const job of jobList) {
                const key  = job.assignedStaffUuid || job.assignedStaffName || UNASSIGNED_KEY;
                const name = job.assignedStaffName || (job.assignedStaffUuid ? 'Unnamed staff' : 'Unassigned');
                if (!groups.has(key)) {
                    groups.set(key, { key, uuid: job.assignedStaffUuid ?? null, name, jobs: [] });
                }
                groups.get(key).jobs.push(job);
            }
            const ordered = [...groups.values()]
                .filter(g => g.key !== UNASSIGNED_KEY)
                .sort((a, b) => a.name.localeCompare(b.name));
            if (groups.has(UNASSIGNED_KEY)) ordered.push(groups.get(UNASSIGNED_KEY));
            for (const g of ordered) g.jobs = sortJobs(g.jobs);
            return ordered;
        };

        // Seed leave-only staff into the active section
        const activeStaffGroups = buildStaffGroups(activeJobList);
        const staffByUuid = Object.fromEntries((staffList ?? []).map(s => [s.uuid, s]));
        const activeKeys = new Set(activeStaffGroups.map(g => g.key));
        for (const staffUuid of Object.keys(leavePlansByStaff || {})) {
            const leaves = leavePlansByStaff[staffUuid];
            if (!leaves || leaves.length === 0) continue;
            if (activeKeys.has(staffUuid)) continue;
            const s = staffByUuid[staffUuid];
            const name = s?.fullName
                || (s ? `${s.firstName ?? ''} ${s.lastName ?? ''}`.trim() : null)
                || 'On leave';
            activeStaffGroups.splice(activeStaffGroups.length - (activeKeys.has(UNASSIGNED_KEY) ? 1 : 0), 0,
                { key: staffUuid, uuid: staffUuid, name, jobs: [] });
        }

        const completedStaffGroups = buildStaffGroups(completedJobList);

        const sectionList = [
            { sectionKey: SECTION_ACTIVE,    sectionName: 'Active Jobs',    staffGroups: activeStaffGroups },
        ];
        if (completedStaffGroups.length > 0) {
            sectionList.push(
                { sectionKey: SECTION_COMPLETED, sectionName: 'Completed Jobs', staffGroups: completedStaffGroups },
            );
        }

        // Colour assignment: one palette slot per unique staff key (across both sections)
        const colourMap = {};
        const used = new Set();
        const allStaffKeys = new Set();
        for (const sec of sectionList) {
            for (const g of sec.staffGroups) {
                allStaffKeys.add(g.key);
            }
        }
        for (const key of [...allStaffKeys].sort()) {
            if (key === UNASSIGNED_KEY) {
                colourMap[key] = UNASSIGNED_COLOUR;
                continue;
            }
            let idx = hashString(key) % STAFF_PALETTE.length;
            let tries = 0;
            while (used.has(idx) && tries < STAFF_PALETTE.length) {
                idx = (idx + 1) % STAFF_PALETTE.length;
                tries++;
            }
            used.add(idx);
            colourMap[key] = STAFF_PALETTE[idx];
        }

        return { sections: sectionList, staffColourMap: colourMap };
    }, [displayableJobs, leavePlansByStaff, staffList]);

    // Legacy flat accessors used by detail drawer + empty state
    const staffGroups = sections.flatMap(s => s.staffGroups);
    const sortedJobs  = staffGroups.flatMap(g => g.jobs);

    // ── Schedule update ────────────────────────────────────────────────────────
    const handleScheduleUpdate = useCallback(async (job, newStart, newEnd, newMinutes) => {
        setSaving(s => ({ ...s, [job.uuid]: 'schedule' }));
        try {
            await dispatch(updateJob({
                uuid: job.uuid,
                data: {
                    scheduledStartDate:       toIsoDate(newStart),
                    scheduledEndDate:         toIsoDate(newEnd),
                    estimatedDurationMinutes: newMinutes,
                },
            })).unwrap();
            setToast({ type: 'success', msg: `${job.jobNumber} rescheduled` });
        } catch {
            setToast({ type: 'error', msg: `Save failed for ${job.jobNumber}` });
        } finally {
            setSaving(s => { const n = { ...s }; delete n[job.uuid]; return n; });
        }
    }, [dispatch]);

    // ── Priority update ────────────────────────────────────────────────────────
    const handlePriorityChange = useCallback(async (job, newPriority) => {
        setSaving(s => ({ ...s, [job.uuid]: 'priority' }));
        try {
            await dispatch(updateJob({
                uuid: job.uuid,
                data: { priority: newPriority },
            })).unwrap();
            setToast({ type: 'success', msg: `${job.jobNumber} → ${newPriority}` });
        } catch {
            setToast({ type: 'error', msg: `Priority update failed for ${job.jobNumber}` });
        } finally {
            setSaving(s => { const n = { ...s }; delete n[job.uuid]; return n; });
        }
    }, [dispatch]);

    // ── Planner order update ───────────────────────────────────────────────────
    const handlePlannerOrderChange = useCallback(async (job, newOrder) => {
        setSaving(s => ({ ...s, [job.uuid]: 'plannerOrder' }));
        try {
            await dispatch(updateJobPlannerOrder({
                uuid: job.uuid,
                plannerOrder: newOrder,
            })).unwrap();
        } catch {
            setToast({ type: 'error', msg: `Order update failed for ${job.jobNumber}` });
        } finally {
            setSaving(s => { const n = { ...s }; delete n[job.uuid]; return n; });
        }
    }, [dispatch]);


    // ── Direct date edit (pencil button) ──────────────────────────────────────
    const handleDateEdit = useCallback(async (job, newIsoDate) => {
        const newStart = startOfDay(parseLocalDate(newIsoDate));
        const durationDays = getDurationDays(job);
        const newEnd = addDays(newStart, Math.ceil(durationDays));
        const newMinutes = job.estimatedDurationMinutes
            ?? Math.round(durationDays * 8 * 60);
        setSaving(s => ({ ...s, [job.uuid]: 'schedule' }));
        try {
            await dispatch(updateJob({
                uuid: job.uuid,
                data: {
                    scheduledStartDate:       toIsoDate(newStart),
                    scheduledEndDate:         toIsoDate(newEnd),
                    estimatedDurationMinutes: newMinutes,
                },
            })).unwrap();
            setToast({ type: 'success', msg: `${job.jobNumber} start date updated` });
        } catch {
            setToast({ type: 'error', msg: `Save failed for ${job.jobNumber}` });
        } finally {
            setSaving(s => { const n = { ...s }; delete n[job.uuid]; return n; });
        }
    }, [dispatch]);

    // ── Staff assignment (from planner row) ─────────────────────────────────
    const handleStaffAssign = useCallback(async (job, staffUuid) => {
        setSaving(s => ({ ...s, [job.uuid]: 'staff' }));
        try {
            await dispatch(assignStaff({ uuid: job.uuid, staffUuid })).unwrap();
            const staffMember = (staffList ?? []).find(s => s.uuid === staffUuid);
            const staffName = staffMember?.fullName
                || (staffMember ? `${staffMember.firstName ?? ''} ${staffMember.lastName ?? ''}`.trim() : 'staff');
            setToast({ type: 'success', msg: `${job.jobNumber} → ${staffName}` });
        } catch {
            setToast({ type: 'error', msg: `Staff assignment failed for ${job.jobNumber}` });
        } finally {
            setSaving(s => { const n = { ...s }; delete n[job.uuid]; return n; });
        }
    }, [dispatch, staffList]);

    const handleStaffUnassign = useCallback(async (job) => {
        setSaving(s => ({ ...s, [job.uuid]: 'staff' }));
        try {
            await dispatch(unassignStaff(job.uuid)).unwrap();
            setToast({ type: 'success', msg: `${job.jobNumber} unassigned` });
        } catch {
            setToast({ type: 'error', msg: `Unassign failed for ${job.jobNumber}` });
        } finally {
            setSaving(s => { const n = { ...s }; delete n[job.uuid]; return n; });
        }
    }, [dispatch]);

    // ── Timeline geometry ──────────────────────────────────────────────────────
    const days       = Array.from({ length: totalDays }, (_, i) => addDays(originDate, i));
    const todayOff   = diffDays(today, originDate);
    const todayLineX = todayOff * cellPx + cellPx / 2;
    const totalGridW = totalDays * cellPx;

    /**
     * Put today at the left edge, with a couple of days of context before it.
     *
     * 🔑 The range runs from a fortnight BEFORE today to a fortnight after, so the
     * timeline's own start is two weeks of the past — which is where it opened, showing
     * empty columns while today's work sat off-screen to the right. The range is
     * deliberately wide; where it OPENS is a different decision.
     */
    const scrollToToday = useCallback(() => {
        const hx = timelineScrollRef.current;
        if (!hx) return;
        const lead = Math.min(todayOff, TODAY_LEAD_DAYS);
        hx.scrollLeft = Math.max(0, (todayOff - lead) * cellPx);
    }, [todayOff, cellPx]);

    // On open, and whenever the zoom or the panned window changes the arithmetic.
    useEffect(() => { scrollToToday(); }, [scrollToToday]);

    const zoomIn   = () => setCellPx(p => Math.min(MAX_CELL_PX, p + 20));
    const zoomOut  = () => setCellPx(p => Math.max(MIN_CELL_PX, p - 20));
    const panLeft  = () => setOffsetDays(o => o - 7);
    const panRight = () => setOffsetDays(o => o + 7);
    // Today resets the window AND scrolls back to it — the offset alone left the view
    // wherever it had been dragged to.
    const goToday  = () => { setOffsetDays(0); scrollToToday(); };

    const monthLabels = [];
    days.forEach((d, i) => {
        if (i === 0 || d.getDate() === 1)
            monthLabels.push({ label: fmtMonth(d), x: i * cellPx });
    });

    return (
        <Layout>
            <div className="flex flex-col h-full bg-gray-50 overflow-hidden">

                {/* ── Toolbar ── */}
                <JobsToolbar
                    activeView="planner"
                    onSwitchView={(id) => {
                        if (id === 'kanban') {
                            onBack?.();
                        } else if (id === 'tablet') {
                            (onSwitchToTablet ?? onBack)?.();
                        }
                    }}
                    title="Job Planner"
                    onRefresh={() => { dispatch(fetchJobs()); queuedJobs.reload(); }}
                    refreshing={jobsLoading || queuedJobs.loading}
                    titleExtras={
                        <PagedSearchControls
                            className="w-full max-w-lg"
                            search={queuedBox.text}
                            onSearch={queuedBox.setText}
                            error={queuedBox.error}
                            page={queuedJobs.page}
                            totalPages={queuedJobs.totalPages}
                            total={queuedJobs.total}
                            size={queuedJobs.size}
                            onPage={queuedJobs.setPage}
                            placeholder="Queued jobs — name or rego"
                            searchLabel="Search queued jobs in the planner"
                            pagerLabel="queued jobs"
                            alwaysShowPager
                        />
                    }
                    rightExtras={
                        <>
                            <button
                                onClick={goToday}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-100 hover:bg-violet-200 rounded-lg text-xs font-semibold text-violet-700 transition-colors shrink-0"
                            >
                                <Calendar size={13} />
                                Today
                            </button>

                            <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 shrink-0">
                                <button onClick={panLeft} className="p-1.5 rounded-lg hover:bg-white transition-colors" title="← 7 days">
                                    <ChevronLeft size={15} />
                                </button>
                                <button onClick={panRight} className="p-1.5 rounded-lg hover:bg-white transition-colors" title="→ 7 days"><ChevronRight size={15} /></button>
                            </div>

                            <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 shrink-0">
                                <button onClick={zoomOut} className="p-1.5 rounded-lg hover:bg-white transition-colors" title="Zoom out"><ZoomOut size={15} /></button>
                                <span className="text-xs font-mono text-gray-500 w-12 text-center">{cellPx}px</span>
                                <button onClick={zoomIn}  className="p-1.5 rounded-lg hover:bg-white transition-colors" title="Zoom in"><ZoomIn  size={15} /></button>
                            </div>
                        </>
                    }
                />

                {/* ── Toast ── */}
                {toast && (
                    <div className={`fixed top-4 right-4 z-50 flex items-center gap-2.5 px-4 py-3 rounded-xl shadow-lg text-sm font-medium
                        ${toast.type === 'success'
                        ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                        : 'bg-red-50 border border-red-200 text-red-800'}`}>
                        {toast.type === 'success'
                            ? <Clock size={14} />
                            : <AlertTriangle size={14} />}
                        {toast.msg}
                    </div>
                )}

                {/* ── Main ── */}
                <div className="flex flex-1 overflow-hidden">

                    {/* Left label column */}
                    <div className="shrink-0 flex flex-col border-r bg-white shadow-sm" style={{ width: LABEL_W }}>

                        {/* Spacer header — same height as month row + day header */}
                        <div
                            className="border-b bg-gray-50 flex items-end px-3 pb-2 shrink-0"
                            style={{ height: HEADER_H + 22 }}
                        >
                            <div className="flex items-center w-full">
                                <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Job / Priority / Staff / Planner #</span>
                            </div>
                        </div>

                        {/* Section → Staff → Job rows */}
                        <div ref={labelScrollRef} className="overflow-y-auto flex-1">
                            {plannerLoading && staffGroups.length === 0 && (
                                <LaneLoading className="p-3" label="Loading active jobs" />
                            )}
                            {staffGroups.length === 0 && !plannerLoading && (
                                <div className="flex flex-col items-center justify-center h-40 text-gray-400 gap-2">
                                    <GanttChart size={28} className="opacity-30" />
                                    <p className="text-sm">No active jobs</p>
                                </div>
                            )}

                            {sections.map(section => {
                                const isSectionCollapsed = !!collapsedSections[section.sectionKey];
                                const sectionJobCount = section.staffGroups.reduce((sum, g) => sum + g.jobs.length, 0);
                                const isActive = section.sectionKey === SECTION_ACTIVE;
                                return (
                                    <div key={section.sectionKey}>
                                        {/* Section header — Active / Completed */}
                                        <button
                                            type="button"
                                            onClick={() => toggleSection(section.sectionKey)}
                                            className={`flex items-center gap-2 px-3 w-full text-left border-b transition
                                                ${isActive
                                                ? 'bg-violet-100/80 hover:bg-violet-100 border-violet-200'
                                                : 'bg-emerald-100/80 hover:bg-emerald-100 border-emerald-200'}`}
                                            style={{ height: SECTION_HEADER_H }}
                                            title={isSectionCollapsed ? 'Expand section' : 'Collapse section'}
                                        >
                                            {isSectionCollapsed
                                                ? <ChevronRight size={15} className="text-gray-600 shrink-0" />
                                                : <ChevronDown  size={15} className="text-gray-600 shrink-0" />}
                                            {isActive
                                                ? <Clock size={13} className="text-violet-600 shrink-0" />
                                                : <Check size={13} className="text-emerald-600 shrink-0" />}
                                            <span className={`text-[12px] font-bold uppercase tracking-wide
                                                ${isActive ? 'text-violet-800' : 'text-emerald-800'}`}>
                                                {section.sectionName}
                                            </span>
                                            <span className={`ml-auto text-[10px] font-bold rounded-full px-2 py-0.5 shrink-0
                                                ${isActive
                                                ? 'bg-violet-200/80 text-violet-700'
                                                : 'bg-emerald-200/80 text-emerald-700'}`}>
                                                {sectionJobCount}
                                            </span>
                                        </button>

                                        {!isSectionCollapsed && section.staffGroups.map(group => {
                                            const colour = staffColourMap[group.key];
                                            const isGroupCollapsed = !!collapsedGroups[group.key];
                                            return (
                                                <div key={group.key}>
                                                    {/* Staff group header */}
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleGroup(group.key)}
                                                        className={`flex items-center gap-2 px-3 pl-6 border-b border-gray-200 w-full text-left
                                                            hover:brightness-95 transition ${colour.tint}`}
                                                        style={{ height: GROUP_HEADER_H }}
                                                        title={isGroupCollapsed ? 'Expand' : 'Collapse'}
                                                    >
                                                        {isGroupCollapsed
                                                            ? <ChevronRight size={14} className="text-gray-500 shrink-0" />
                                                            : <ChevronDown  size={14} className="text-gray-500 shrink-0" />}
                                                        <div
                                                            className="w-3 h-3 rounded-full border border-white/80 shadow-sm shrink-0"
                                                            style={{ backgroundColor: colour.swatch }}
                                                        />
                                                        {group.key === UNASSIGNED_KEY
                                                            ? <Users size={12} className="text-gray-500 shrink-0" />
                                                            : <User  size={12} className="text-gray-500 shrink-0" />}
                                                        <span className="text-[12px] font-bold text-gray-800 truncate">{group.name}</span>
                                                        <span className="ml-auto text-[10px] font-semibold text-gray-500 bg-white/70 rounded-full px-2 py-0.5 shrink-0">
                                                            {group.jobs.length}
                                                        </span>
                                                    </button>

                                                    {/* Collapsed compact summary row */}
                                                    {isGroupCollapsed && group.jobs.length > 0 && (
                                                        <div
                                                            className={`flex items-center gap-2 px-3 pl-8 border-b border-gray-100 text-[10px] text-gray-400 ${colour.tint} bg-opacity-30`}
                                                            style={{ height: COLLAPSED_ROW_H }}
                                                        >
                                                            <span className="font-medium text-gray-500">
                                                                {group.jobs.length} job{group.jobs.length !== 1 ? 's' : ''}
                                                            </span>
                                                            <span className="text-gray-300">·</span>
                                                            <span className="truncate text-gray-400">
                                                                {group.jobs.map(j => j.jobNumber).join(', ')}
                                                            </span>
                                                        </div>
                                                    )}

                                                    {!isGroupCollapsed && group.jobs.map(job => (
                                                        <div
                                                            key={job.uuid}
                                                            className={`flex flex-col justify-center gap-0.5 px-3 py-2 border-b cursor-pointer transition-colors
                                                    ${selected?.uuid === job.uuid ? 'bg-violet-50' : 'hover:bg-gray-50'}`}
                                                            style={{ height: ROW_H, minHeight: ROW_H }}
                                                            onClick={() => setSelected(selected?.uuid === job.uuid ? null : job)}
                                                        >
                                                            {/* Line 1: priority + job number + status badge + planner order */}
                                                            <div className="flex items-center gap-2 w-full">
                                                                <PriorityDropdown
                                                                    priority={job.priority ?? 'NORMAL'}
                                                                    saving={saving[job.uuid] === 'priority'}
                                                                    onChangePriority={(newP) => handlePriorityChange(job, newP)}
                                                                />
                                                                <span className="text-xs font-bold text-gray-800 shrink-0">{job.jobNumber}</span>
                                                                <StatusBadge status={job.status} />
                                                                {saving[job.uuid] === 'schedule' && (
                                                                    <RefreshCw size={10} className="animate-spin text-violet-400 shrink-0" />
                                                                )}
                                                                <div className="ml-auto shrink-0">
                                                                    <PlannerOrderEditor
                                                                        value={job.plannerOrder ?? null}
                                                                        saving={saving[job.uuid] === 'plannerOrder'}
                                                                        onSave={(n) => handlePlannerOrderChange(job, n)}
                                                                    />
                                                                </div>
                                                            </div>

                                                            {/* Line 2: customer name */}
                                                            <div className="flex items-center gap-1 text-[11px] text-gray-600 w-full">
                                                                <User size={10} className="shrink-0 text-gray-400" />
                                                                <span className="truncate">{job.customerName || '—'}</span>
                                                            </div>

                                                            {/* Line 3: staff assignment */}
                                                            <div className="flex items-center gap-1 w-full">
                                                                <StaffAssignDropdown
                                                                    job={job}
                                                                    staffList={staffList}
                                                                    staffColourMap={staffColourMap}
                                                                    saving={saving[job.uuid] === 'staff'}
                                                                    onAssign={handleStaffAssign}
                                                                    onUnassign={handleStaffUnassign}
                                                                />
                                                            </div>

                                                            {/* Line 4: rego + inline start date editor */}
                                                            <div className="flex items-center gap-2 w-full">
                                                                <div className="flex items-center gap-1 text-[10px] text-gray-400 shrink-0">
                                                                    <Car size={10} className="shrink-0" />
                                                                    <span className="font-mono">{job.vehicleRego || job.vehicleDescription || '—'}</span>
                                                                </div>
                                                                <div className="ml-auto shrink-0">
                                                                    <DateEditField
                                                                        label="Start"
                                                                        isoDate={job.scheduledStartDate || null}
                                                                        saving={saving[job.uuid] === 'schedule'}
                                                                        onSave={(iso) => handleDateEdit(job, iso)}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Scrollable timeline — click and hold anywhere to pan */}
                    <div
                        ref={timelineScrollRef}
                        data-testid="planner-timeline"
                        onMouseDown={startPan}
                        title="Click and hold to move the calendar"
                        className={`flex-1 overflow-x-auto overflow-y-hidden
                            ${panning ? 'cursor-grabbing select-none [&_*]:cursor-grabbing' : 'cursor-grab'}`}
                    >
                        <div style={{ width: Math.max(totalGridW, 1), minWidth: '100%' }} className="flex flex-col h-full">

                            {/* Month row */}
                            <div className="flex bg-gray-50 border-b shrink-0 relative" style={{ height: 22 }}>
                                {monthLabels.map(({ label, x }, i) => (
                                    <div
                                        key={i}
                                        className="absolute top-0 h-full flex items-center pl-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wider"
                                        style={{ left: x }}
                                    >
                                        {label}
                                    </div>
                                ))}
                            </div>

                            {/* Day headers */}
                            <div className="flex bg-gray-50 border-b shrink-0" style={{ height: HEADER_H }}>
                                {days.map((d, i) => {
                                    const isToday   = diffDays(d, today) === 0;
                                    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                                    return (
                                        <div
                                            key={i}
                                            className={`shrink-0 flex flex-col items-center justify-center border-r text-center
                                                ${isToday ? 'bg-violet-50' : isWeekend ? 'bg-gray-100' : ''}`}
                                            style={{ width: cellPx }}
                                        >
                                            <span className={`text-[10px] font-medium uppercase tracking-wider
                                                ${isToday ? 'text-violet-600' : 'text-gray-400'}`}>
                                                {d.toLocaleDateString('en-AU', { weekday: 'short' })}
                                            </span>
                                            <span className={`text-sm font-bold leading-tight
                                                ${isToday ? 'text-violet-700' : isWeekend ? 'text-gray-400' : 'text-gray-700'}`}>
                                                {fmtShortDay(d)}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Grid + bars */}
                            <div ref={gridScrollRef} className="relative overflow-y-auto flex-1">

                                {/* Today vertical line */}
                                {todayOff >= 0 && todayOff < totalDays && (
                                    <div
                                        className="absolute top-0 bottom-0 w-0.5 bg-violet-400 opacity-60 pointer-events-none z-20"
                                        style={{ left: todayLineX }}
                                    />
                                )}

                                {sections.map(section => {
                                    const isSectionCollapsed = !!collapsedSections[section.sectionKey];
                                    const isActive = section.sectionKey === SECTION_ACTIVE;
                                    return (
                                        <div key={section.sectionKey}>
                                            {/* Section header row — matches left column */}
                                            <div
                                                className={`relative flex items-center border-b cursor-pointer
                                                    ${isActive
                                                    ? 'bg-violet-100/80 border-violet-200'
                                                    : 'bg-emerald-100/80 border-emerald-200'}`}
                                                style={{ height: SECTION_HEADER_H, width: totalGridW }}
                                                onClick={() => toggleSection(section.sectionKey)}
                                            >
                                                <div className="sticky left-0 flex items-center gap-2 px-3 h-full z-10">
                                                    {isSectionCollapsed
                                                        ? <ChevronRight size={15} className="text-gray-600 shrink-0" />
                                                        : <ChevronDown  size={15} className="text-gray-600 shrink-0" />}
                                                    <span className={`text-[12px] font-bold uppercase tracking-wide whitespace-nowrap
                                                        ${isActive ? 'text-violet-800' : 'text-emerald-800'}`}>
                                                        {section.sectionName}
                                                    </span>
                                                </div>
                                            </div>

                                            {!isSectionCollapsed && section.staffGroups.map(group => {
                                                const colour = staffColourMap[group.key];
                                                const isCollapsed = !!collapsedGroups[group.key];
                                                const groupLeave = group.uuid ? (leavePlansByStaff[group.uuid] || []) : [];
                                                return (
                                                    <div key={group.key}>
                                                        {/* Staff group header row */}
                                                        <div
                                                            className={`relative flex border-b border-gray-200 cursor-pointer ${colour.tint}`}
                                                            style={{ height: GROUP_HEADER_H, width: totalGridW }}
                                                            onClick={() => toggleGroup(group.key)}
                                                            title={isCollapsed ? 'Expand' : 'Collapse'}
                                                        >
                                                            <div
                                                                className="sticky left-0 flex items-center gap-2 px-3 pl-6 h-full z-10"
                                                                style={{ backgroundColor: 'transparent' }}
                                                            >
                                                                {isCollapsed
                                                                    ? <ChevronRight size={13} className="text-gray-500 shrink-0" />
                                                                    : <ChevronDown  size={13} className="text-gray-500 shrink-0" />}
                                                                <div
                                                                    className="w-2.5 h-2.5 rounded-full border border-white/80 shrink-0"
                                                                    style={{ backgroundColor: colour.swatch }}
                                                                />
                                                                <span className="text-[11px] font-semibold text-gray-700 whitespace-nowrap">
                                                                    {group.name}
                                                                </span>
                                                                {groupLeave.length > 0 && (
                                                                    <span className="ml-2 text-[10px] text-gray-500 italic whitespace-nowrap">
                                                                        · {groupLeave.length} leave {groupLeave.length === 1 ? 'period' : 'periods'} in view
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Collapsed compact row — all bars on one line */}
                                                        {isCollapsed && group.jobs.length > 0 && (
                                                            <div
                                                                className={`relative border-b border-gray-100 flex shrink-0 ${colour.tint} bg-opacity-30`}
                                                                style={{ height: COLLAPSED_ROW_H }}
                                                            >
                                                                {days.map((d, ci) => {
                                                                    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                                                                    return (
                                                                        <div
                                                                            key={ci}
                                                                            className={`shrink-0 h-full border-r ${isWeekend ? 'bg-gray-100/40' : ''}`}
                                                                            style={{ width: cellPx }}
                                                                        />
                                                                    );
                                                                })}
                                                                {group.jobs.map(job => (
                                                                    <GanttBar
                                                                        key={job.uuid}
                                                                        job={job}
                                                                        cellPx={cellPx}
                                                                        originDate={originDate}
                                                                        staffColour={colour}
                                                                        onUpdate={handleScheduleUpdate}
                                                                        onSelect={setSelected}
                                                                        compact
                                                                    />
                                                                ))}
                                                            </div>
                                                        )}

                                                        {/* Relative wrapper so the leave overlay can span all rows */}
                                                        {!isCollapsed && (
                                                            <div className="relative" style={{ width: totalGridW }}>
                                                                {group.jobs.map((job, rowIdx) => (
                                                                    <div
                                                                        key={job.uuid}
                                                                        className={`relative border-b flex shrink-0
                                                                            ${selected?.uuid === job.uuid
                                                                            ? 'bg-violet-50/40'
                                                                            : rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}`}
                                                                        style={{ height: ROW_H }}
                                                                    >
                                                                        {days.map((d, ci) => {
                                                                            const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                                                                            return (
                                                                                <div
                                                                                    key={ci}
                                                                                    className={`shrink-0 h-full border-r ${isWeekend ? 'bg-gray-100/60' : ''}`}
                                                                                    style={{ width: cellPx }}
                                                                                />
                                                                            );
                                                                        })}

                                                                        <GanttBar
                                                                            job={job}
                                                                            cellPx={cellPx}
                                                                            originDate={originDate}
                                                                            staffColour={colour}
                                                                            onUpdate={handleScheduleUpdate}
                                                                            onSelect={setSelected}
                                                                        />
                                                                    </div>
                                                                ))}

                                                                {group.jobs.length === 0 && groupLeave.length > 0 && (
                                                                    <div
                                                                        className="relative border-b flex shrink-0 bg-white"
                                                                        style={{ height: ROW_H }}
                                                                    >
                                                                        {days.map((d, ci) => {
                                                                            const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                                                                            return (
                                                                                <div
                                                                                    key={ci}
                                                                                    className={`shrink-0 h-full border-r ${isWeekend ? 'bg-gray-100/60' : ''}`}
                                                                                    style={{ width: cellPx }}
                                                                                />
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}

                                                                <LeaveOverlay
                                                                    leavePlans={groupLeave}
                                                                    cellPx={cellPx}
                                                                    originDate={originDate}
                                                                    windowFromDate={originDate}
                                                                    windowToDate={addDays(originDate, totalDays - 1)}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })}

                                {sortedJobs.length === 0 && plannerLoading && (
                                    <div className="flex items-center justify-center h-48 text-gray-400 gap-2">
                                        <RefreshCw size={18} className="animate-spin" />
                                        <span className="text-sm">Loading jobs…</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Detail drawer ── */}
                {selected && (
                    <div className="shrink-0 border-t bg-white px-5 py-3 flex items-center gap-6 shadow-lg">
                        <div>
                            <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-sm font-bold text-gray-900">{selected.jobNumber}</span>
                                <StatusBadge status={selected.status} />
                                {selected.priority && (
                                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded
                                        ${selected.priority === 'URGENT' ? 'bg-red-100 text-red-700'
                                        : selected.priority === 'HIGH'   ? 'bg-orange-100 text-orange-700'
                                            : selected.priority === 'LOW'    ? 'bg-blue-100 text-blue-600'
                                                : 'bg-gray-100 text-gray-500'}`}>
                                        {selected.priority}
                                    </span>
                                )}
                            </div>
                            <div className="text-xs text-gray-500">
                                {selected.customerName} · {selected.vehicleRego || selected.vehicleDescription || '—'}
                            </div>
                        </div>

                        <div className="w-px h-8 bg-gray-200" />

                        <div className="text-xs text-gray-600 space-y-0.5">
                            <div className="flex items-center gap-1.5">
                                <Calendar size={11} className="text-gray-400" />
                                <span className="text-gray-400">Scheduled:</span>
                                <span className="font-semibold text-gray-700">
                                    {selected.scheduledStartDate
                                        ? fmtDay(parseLocalDate(selected.scheduledStartDate))
                                        : '—'}
                                    {selected.scheduledEndDate
                                        ? ` → ${fmtDay(parseLocalDate(selected.scheduledEndDate))}`
                                        : ''}
                                </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Clock size={11} className="text-gray-400" />
                                <span className="text-gray-400">Duration:</span>
                                <span className="font-semibold text-gray-700">
                                    {getDurationDays(selected).toFixed(1)} days
                                    {selected.estimatedDurationMinutes
                                        ? ` (${selected.estimatedDurationMinutes} min)`
                                        : ''}
                                </span>
                            </div>
                        </div>

                        <div className="w-px h-8 bg-gray-200" />

                        <div className="flex items-center gap-1.5 text-xs text-gray-600 min-w-[180px]">
                            <StaffAssignDropdown
                                job={selected}
                                staffList={staffList}
                                staffColourMap={staffColourMap}
                                saving={saving[selected.uuid] === 'staff'}
                                onAssign={handleStaffAssign}
                                onUnassign={handleStaffUnassign}
                            />
                        </div>

                        <div className="flex-1" />
                        <button
                            onClick={() => setSelected(null)}
                            className="text-xs text-gray-400 hover:text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            Dismiss
                        </button>
                    </div>
                )}

                {/* ── Legend (fixed height; scrolls internally if content grows) ── */}
                <div
                    className="bg-white border-t shrink-0 overflow-y-auto"
                    style={{ height: LEGEND_H }}
                >
                    <div className="flex items-center gap-3 px-5 py-2 text-[11px] text-gray-400 flex-wrap min-h-full">
                        <span className="font-semibold text-gray-500">Staff:</span>
                        {staffGroups.length === 0 && (
                            <span className="italic opacity-70">No jobs to show</span>
                        )}
                        {staffGroups.map(group => {
                            const colour = staffColourMap[group.key];
                            return (
                                <div key={group.key} className="flex items-center gap-1.5">
                                    <div className={`w-3 h-3 rounded ${colour.bg} border ${colour.border}`} />
                                    <span className="text-gray-600">{group.name}</span>
                                    <span className="text-gray-400">({group.jobs.length})</span>
                                </div>
                            );
                        })}
                        <div className="w-px h-3 bg-gray-200 mx-1" />
                        <span className="font-semibold text-gray-500">Priority override:</span>
                        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-red-500" /><span>Urgent</span></div>
                        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-orange-400" /><span>High</span></div>
                        <div className="w-px h-3 bg-gray-200 mx-1" />
                        <div className="flex items-center gap-1.5"><div className="w-0.5 h-3 bg-violet-400 rounded" /><span>Today</span></div>
                        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-gray-100 border border-gray-300" /><span>Weekend</span></div>
                        <span className="ml-auto opacity-70">Drag to reschedule · resize right edge to change duration · sorted by priority + planner #</span>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default JobsPlannerPage;